<template>
    <PlanbordGridRow sticky>
        <PlanbordGridLeftDataColumns>
            <PlanbordGridCell header centeredVertically>{{ label }}</PlanbordGridCell>
            <PlanbordFunctionCell header />
            <PlanbordCommentCell header />
            <PlanbordStampStartDateCell header />
        </PlanbordGridLeftDataColumns>

        <!-- Render weekddays -->
        <template v-for="day in roster.days" :key="day.date.getTime()">
            <PlanbordGridCell class="day" :class="headerClass(day.date)" :title="headerTitle(day.date)" header>
                <div class="day-block" v-if="isRosterEndedOnDate(day.date)">
                    {{ day.dayAbbreviation }} {{ day.formattedDateOnly }}
                    <span
                        v-if="getHolidayDescriptions(day)"
                        :title="getHolidayDescriptions(day)"
                        class="mdi mdi-beach holiday-title"
                    ></span>
                    <span v-if="shouldShowUnpublishedPartVisibleBadge(day.date)" title="Rooster wordt getoond, alleen zicht" class="unpublished-visible">👁</span>
                </div>
                <a v-else class="day-block" :href="publishShiftLink(day.date)" target="_blank">
                    {{ day.dayAbbreviation }} {{ day.formattedDateOnly }}
                    <span
                        v-if="getHolidayDescriptions(day)"
                        :title="getHolidayDescriptions(day)"
                        class="mdi mdi-beach holiday-title"
                    ></span>
                    <span v-if="shouldShowUnpublishedPartVisibleBadge(day.date)" title="Rooster wordt getoond, alleen zicht" class="unpublished-visible">👁</span>
                </a>
            </PlanbordGridCell>
        </template>
    </PlanbordGridRow>
</template>

<script lang="ts">
    import {RosterDay, RosterData} from '@/types/types';
    import HolidayUtil from '@/util/holidayutil';
    import PlanbordGridRow from '@/components/Planbord/PlanbordGridRow.vue';
    import PlanbordGridLeftDataColumns from '@/components/Planbord/PlanbordGridLeftDataColumns.vue';
    import PlanbordGridCell from '@/components/Planbord/PlanbordGridCell.vue';
    import PlanbordFunctionCell from '@/components/Planbord/PlanbordFunctionCell.vue';
    import PlanbordCommentCell from '@/components/Planbord/PlanbordCommentCell.vue';
    import PlanbordStampStartDateCell from '@/components/Planbord/PlanbordStampStartDateCell.vue';
    import {Component, Vue, Prop} from 'vue-facing-decorator';
    import RoosterplanningUrlBuilder from '@/util/RoosterplanningUrlBuilder';

    @Component({
        components: {
            PlanbordGridRow,
            PlanbordGridLeftDataColumns,
            PlanbordGridCell,
            PlanbordFunctionCell,
            PlanbordCommentCell,
            PlanbordStampStartDateCell,
        },
    })
    export default class WorkloadRequirementsHead extends Vue {
        @Prop({required: true}) public roster!: RosterData;
        @Prop({default: 'Dienst'}) public label!: string;

        public shouldShowUnpublishedPartVisibleBadge(day: Date): boolean {
            if (!this.roster.unpublishedPartVisibleUntil || (this.roster.publishedUntil && (this.roster.publishedUntil >= day))) {
                return false;
            }

            return this.roster.unpublishedPartVisibleUntil >= day;
        }

        public headerClass(day: Date): string {
            if (this.roster.endDate && this.roster.endDate <= day) {
                return 'ended';
            }

            if (this.roster.publishedUntil && (this.roster.publishedUntil >= day)) {
                if (this.roster.deriveRightsUntil && (this.roster.deriveRightsUntil < day)) {
                    return 'published-no-rights';
                }

                return 'published';
            }

            if (this.roster.proposedUntil && this.roster.proposedUntil >= day) {
                return 'proposed';
            }

            return 'inConstruction';
        }

        public headerTitle(day: Date): string {
            if (this.roster.endDate && this.roster.endDate <= day) {
                return 'Beëindigd rooster';
            }

            if (this.roster.publishedUntil && (this.roster.publishedUntil >= day)) {
                if (this.roster.deriveRightsUntil && (this.roster.deriveRightsUntil < day)) {
                    return 'Actief, nog niet definitief rooster. Er kunnen geen rechten aan worden ontleend.';
                }

                return 'Actief rooster';
            }

            if (this.roster.unpublishedPartVisibleUntil && this.roster.unpublishedPartVisibleUntil >= day) {
                return 'In ontwikkeling met inzage';
            }

            return 'In ontwikkeling';
        }

        public getHolidayDescriptions(day: RosterDay): string {
            return HolidayUtil.getHolidayNamesAsSingleString(day, this.roster.holidays);
        }

        public isRosterEndedOnDate(date: Date): boolean {
            return !!this.roster.endDate && this.roster.endDate <= date;
        }

        public publishShiftLink(date: Date): string {
            return `${RoosterplanningUrlBuilder.getHostUrl()}?pageid=P_ROSTER_OPENSHIFT&rosterid=${this.roster.rosterId}&date=${(date.valueOf() / 1000).toFixed(0)}`;
        }
    }
</script>

<style lang="scss" scoped>
    .day {
        .day-block {
            display: flex;
            padding: 5px 5px 11px;
            text-decoration: none;
            justify-content: space-between;
            align-items: center;
            color: var(--theme-text-color);

            span.unpublished-visible {
                display: inline-flex;
                background: var(--construction-header-color);
                width: 1.375rem;
                height: 1.375rem;
                justify-content: center;
                align-items: center;
                border-radius: 50%;
                font-size: 1.25rem;
                padding-bottom: 0.25rem;
            }
        }
    }
</style>
